import { Link } from 'react-router-dom'
import { User, UserRole } from '@/types/entities'
import { formatUserName, getInitials } from '@/lib/utils'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { CommandPaletteTrigger } from '@/components/common/CommandPalette'
import { MasqueradePicker } from '@/features/masquerade/components/MasqueradePicker'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { Bell, HelpCircle, Menu } from 'lucide-react'

interface TopNavProps {
  user: User
  onToggleSidebar?: () => void
}

export function TopNav({ user, onToggleSidebar }: TopNavProps) {
  const { signOut } = useAuth()

  const isManager = user.role === UserRole.MANAGER
  const isMasquerading = useAuthStore((s) => s.masquerade !== null)

  return (
    <header className="h-16 border-b border-border/50 bg-card flex items-center justify-between px-6 shadow-subtle">
      {/* Left side - Global search for managers */}
      <div className="flex items-center gap-4">
        <button aria-label="Open navigation" className="lg:hidden" onClick={onToggleSidebar}><Menu className="h-5 w-5" /></button>
        {isManager && <CommandPaletteTrigger />}
        {/* Manager-only to START one, but this must also render DURING a masquerade — the user
            is the artist then, so gating on role alone would unmount the component holding the
            only way back and strand the manager in someone else's account. */}
        {(isManager || isMasquerading) && <MasqueradePicker />}
        {!isManager && (
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        )}
      </div>

      {/* Right side - Actions and User menu */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-[18px] w-[18px]" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
        >
          <Bell className="h-[18px] w-[18px]" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 h-9 px-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-muted text-xs font-medium">
                  {getInitials(user)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden md:inline text-sm">{formatUserName(user)}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{formatUserName(user)}</p>
                <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/app/account">Profile Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>Sign Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

